import { useRef, useEffect } from 'react';
import gsap from 'gsap';

import iconNfl from '../images/icons/1.png';
import iconMarvel from '../images/icons/2.png';
import iconWwe from '../images/icons/3.png';
import iconNba from '../images/icons/4.png';
import iconMlb from '../images/icons/5.png';
import iconUcl from '../images/icons/6.png';
import iconUfc from '../images/icons/7.png';
import iconMls from '../images/icons/8.png';
import iconStarWars from '../images/icons/9.png';
import iconNhl from '../images/icons/10.png';
import iconF1 from '../images/icons/11.png';

import cardRomanReigns from '../images/cards/1.png';
import cardAlexiMoreau from '../images/cards/2.png';
import cardJudge from '../images/cards/3.png';
import cardRheaRipley from '../images/cards/4.png';
import cardMcgregor from '../images/cards/5.png';

const DEFAULT_NAV_ITEMS = [
  { label: 'NFL', image: iconWwe },
  { label: 'Marvel', image: iconNba },
  { label: 'WWE', image: iconNhl },
  { label: 'NBA', image: iconMlb },
  { label: 'MLB', image: iconMarvel },
  { label: 'UCL', image: iconNfl },
  { label: 'UFC', image: iconUcl },
  { label: 'MLS', image: iconUfc },
  { label: 'F1', image: iconStarWars },
  { label: 'NHL', image: iconNhl },
  { label: 'Star Wars', image: iconF1 },
];

const DEFAULT_CARDS = [
  {
    id: 'card-1',
    league: 'WWE - LIMITED',
    name: 'RHEA RIPLEY',
    image: cardRheaRipley,
    accent: '#b25cff',
  },
  {
    id: 'card-2',
    league: 'F1 - ELITE',
    name: 'ALEXI MOREAU',
    image: cardAlexiMoreau,
    accent: '#ffd166',
  },
  {
    id: 'card-3',
    league: 'WWE - HEAD TABLE',
    name: 'TRIBAL CHIEF',
    image: cardRomanReigns,
    accent: '#e0455c',
  },
  {
    id: 'card-4',
    league: 'MLB - ROOKIE',
    name: 'A. JUDGE',
    image: cardJudge,
    accent: '#4d9fff',
  },
  {
    id: 'card-5',
    league: 'UFC - NOTORIOUS',
    name: 'C. MCGREGOR',
    image: cardMcgregor,
    accent: '#ff8a3d',
  },
];

function NavPill({ label, image, active }) {
  return (
    <div className={`nav-pill${active ? ' nav-pill--active' : ''}`}>
      <img src={image} alt={label} className="nav-pill__icon" />
      {label}
    </div>
  );
}

function Card({ league, name, image, accent, featured = false }) {
  const cardRef = useRef(null);

  return (
    <div
      className={`card${featured ? ' card--featured' : ''}`}
      ref={cardRef}
      style={{
        '--card-accent': accent,
        '--card-lift': featured ? '-20px' : '0px',
        '--card-feature-scale': featured ? '1.18' : '1',
      }}
    >
      <div className="card__frame">
        <img src={image} alt={name} className="card__image" />

        <div className="card__overlay">
          <span className="card__league">{league}</span>
        </div>
      </div>
    </div>
  );
}

export default function Banner({
  navItems = DEFAULT_NAV_ITEMS,
  watermark = 'OBC',
  title = 'CARDS & COLLECTIBLES',
  description = `OBC Cards & Collectibles isn't your typical marketplace. This is a high-voltage trading arena where every card drop feels like a casino hand, every auction feels like overtime in a championship game, and every win hits like a buzzer-beater. Built for collectors who crave adrenaline, precision, and control - whether you're here to bid, buy, or build your legacy collection.`,
  primaryCta = { label: 'Start Bidding', onClick: () => {} },
  secondaryCta = { label: 'Shop Buy Now', onClick: () => {} },
  cards = DEFAULT_CARDS,
}) {
  const contentRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.from('.nav-pill', {
        opacity: 0,
        y: -14,
        duration: 0.5,
        stagger: 0.04,
      })
        .from(
          '.hero__watermark',
          { opacity: 0, scale: 1.3, duration: 0.8 },
          '-=0.2'
        )
        .from(
          '.hero__title',
          { opacity: 0, y: 30, duration: 0.7 },
          '-=0.5'
        )
        .from(
          '.hero__desc',
          { opacity: 0, y: 20, duration: 0.5 },
          '-=0.4'
        );
    }, contentRef);

    return () => ctx.revert();
  }, []);

  return (
    <section className="obc-banner" style={{ position: 'relative', overflow: 'hidden' }}>
      <div ref={contentRef} style={{ position: 'relative', zIndex: 1 }}>
        <nav className="nav">
          {navItems.map((item) => (
            <NavPill key={item.label} {...item} />
          ))}
        </nav>

        <div className="hero">
          <div className="hero__watermark">{watermark}</div>
          <h1 className="hero__title">{title}</h1>
          <p className="hero__desc">{description}</p>

          <div className="hero__ctas">
            <button className="btn btn--primary" onClick={primaryCta.onClick}>
              {primaryCta.label}
            </button>

            <button className="btn btn--secondary" onClick={secondaryCta.onClick}>
              {secondaryCta.label}
            </button>
          </div>
        </div>

        <div className="card-row">
          {cards.map((card, index) => (
            <Card key={card.id} featured={index === 2} {...card} />
          ))}
        </div>
      </div>
    </section>
  );
}
